import { useMemo, type ReactNode } from 'react';

import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  type BoxProps,
} from '@mui/material';
import { useDebounce } from 'use-debounce';

import { useControllableState } from '@shared/lib/hooks';

import { SearchIcon } from '../icons';

import { filterGroups } from './filter-groups';
import { GroupRow } from './group-row';
import { Hint } from './hint';
import type { SearchPanelGroup } from './types';
import { ResultsListStyled } from './search-panel.styled';

export type SearchPanelProps = {
  // Search query — controlled (`search` + `onSearchChange`) or uncontrolled
  // (`defaultSearch`, panel owns the input state). `onSearchChange` fires in both.
  search?: string;
  defaultSearch?: string;
  onSearchChange?: (search: string) => void;
  placeholder?: string;
  // Debounce (ms) applied to the local filter only — the input stays responsive
  // while filtering runs on the debounced value. Ignored in external mode.
  debounceMs?: number;
  // Whether to render the search input. Off → the panel just lists all groups
  // with no filtering (the box is hidden). Defaults to on.
  searchable?: boolean;

  groups?: SearchPanelGroup[];
  // Render prop for the right-aligned group meta. Receives live counts so it can
  // render an "N of M" indicator during local filtering; text stays
  // consumer-owned. `visibleItems` equals `totalItems` in external mode.
  renderGroupMeta?: (params: {
    group: SearchPanelGroup;
    totalItems: number;
    visibleItems: number;
  }) => ReactNode;
  // Props forwarded to the scrollable results list container. Use it to cap the
  // height (`{ maxHeight: '50vh' }`) — omit and the list grows with its content.
  listProps?: BoxProps;

  // Selection (flat set of item ids) — controlled (`selectedIds`) or
  // uncontrolled (`defaultSelectedIds`). Either way the panel owns the selection
  // mechanics: toggling a single item, toggling a whole group (all-or-none of
  // its visible items) and deriving each group's checkbox state.
  selectedIds?: Set<string>;
  defaultSelectedIds?: Set<string>;
  onSelectedIdsChange?: (next: Set<string>) => void;

  isLoading?: boolean;
  isError?: boolean;
  emptyHint?: ReactNode; // shown when the query matches nothing
  errorHint?: ReactNode;
};

const EMPTY_GROUPS: SearchPanelGroup[] = [];
const EMPTY_SELECTION = new Set<string>();

export const SearchPanel = (props: SearchPanelProps) => {
  const {
    placeholder,
    groups = EMPTY_GROUPS,
    renderGroupMeta,
    listProps,
    isLoading,
    isError,
    emptyHint,
    errorHint,
    debounceMs = 300,
    searchable = true,
  } = props;

  const [search, setSearch] = useControllableState({
    controlled: props.search,
    defaultValue: props.defaultSearch ?? '',
    onChange: props.onSearchChange,
  });

  const [debouncedSearch] = useDebounce(search, debounceMs);

  const [selectedIds, setSelectedIds] = useControllableState({
    controlled: props.selectedIds,
    defaultValue: props.defaultSelectedIds ?? EMPTY_SELECTION,
    onChange: props.onSelectedIdsChange,
  });

  // Controlled search = external mode: the consumer owns the query and feeds
  // already-filtered groups (client or server side). Uncontrolled = the panel
  // filters locally over the neutral label fields.
  const isExternalSearch = props.search !== undefined;
  // Local filtering runs only when the panel owns the search (searchable and not
  // external) — otherwise groups pass through untouched.
  const isLocalSearch = searchable && !isExternalSearch;
  // NOTE: intentionally not showing a loader while the local filter debounce
  // settles — the list briefly shows results for the previous query, which is
  // fine given our fast responses. Restore this (and `|| isDebouncing` in
  // renderBody) if the debounce window ever feels laggy:
  // const isDebouncing = !isExternalSearch && search !== debouncedSearch;
  const visibleGroups = useMemo(
    () => (isLocalSearch ? filterGroups(groups, debouncedSearch) : groups),
    [isLocalSearch, groups, debouncedSearch],
  );

  // Total item count per group before filtering — feeds the meta count so a
  // group can show "N of M" while locally filtered.
  const totalsById = useMemo(
    () => new Map(groups.map((group) => [group.id, group.items.length])),
    [groups],
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (isError) return <Hint>{errorHint}</Hint>;

    if (visibleGroups.length === 0) return <Hint>{emptyHint}</Hint>;

    return visibleGroups.map((group) => (
      <GroupRow
        key={group.id}
        group={group}
        meta={renderGroupMeta?.({
          group,
          totalItems: totalsById.get(group.id) ?? group.items.length,
          visibleItems: group.items.length,
        })}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
      />
    ));
  };

  return (
    <Stack gap={1.5}>
      {searchable && (
        <TextField
          placeholder={placeholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
      <ResultsListStyled {...listProps}>{renderBody()}</ResultsListStyled>
    </Stack>
  );
};
